import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBrand } from '../context/BrandContext';
import { amplifyApi } from '../services/amplifyApi';
import {
  ArrowLeft,
  Check,
  Crown,
  Star,
  Gift,
  Zap,
  Loader2,
  AlertCircle,
  ExternalLink,
  Sparkles,
  Package
} from 'lucide-react';

const TIER_CONFIG = {
  free: {
    name: 'Free',
    icon: Gift,
    price: '$0',
    period: 'forever',
    description: 'Perfect for getting started',
    color: 'slate',
    features: [
      '3 products per session',
      '10 products per day',
      '50 products per month',
      '5 AI generations per hour',
      '20 background removals per hour',
      'Basic support'
    ]
  },
  pro: {
    name: 'Pro',
    icon: Star,
    price: '$9.99',
    period: '/month',
    description: 'For serious creators',
    color: 'blue',
    popular: true,
    features: [
      '10 products per session',
      '50 products per day',
      '500 products per month',
      '20 AI generations per hour',
      '50 background removals per hour',
      'Priority support',
      'Early access to new features'
    ]
  },
  enterprise: {
    name: 'Enterprise',
    icon: Crown,
    price: '$49.99',
    period: '/month',
    description: 'For power users & businesses',
    color: 'purple',
    features: [
      '50 products per session',
      '500 products per day',
      '10,000 products per month',
      '100 AI generations per hour',
      '200 background removals per hour',
      'Dedicated support',
      'Custom integrations',
      'API access'
    ]
  }
};

function PricingPage() {
  const { isAuthenticated, openLoginModal } = useAuth();
  const brand = useBrand();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentTier, setCurrentTier] = useState('free');
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  // Check for tier param from upgrade modal
  const requestedTier = searchParams.get('tier');

  useEffect(() => {
    if (isAuthenticated) {
      loadCurrentStatus();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    // Auto-trigger checkout if coming from upgrade modal with a tier
    if (requestedTier && isAuthenticated && !loading) {
      handleUpgrade(requestedTier);
    }
  }, [requestedTier, isAuthenticated]);

  const loadCurrentStatus = async () => {
    const [limitsResponse, subscriptionResponse] = await Promise.all([
      amplifyApi.getUserLimits(),
      amplifyApi.getSubscriptionStatus()
    ]);

    if (limitsResponse.success) {
      setCurrentTier(limitsResponse.tier || 'free');
    }

    if (subscriptionResponse.success) {
      setSubscriptionStatus(subscriptionResponse);
    }
  };

  const handleUpgrade = async (tier) => {
    if (!isAuthenticated) {
      openLoginModal('Sign in to upgrade your plan');
      return;
    }

    if (tier === 'free') {
      // Can't "upgrade" to free
      return;
    }

    setLoading(tier);
    setError(null);

    try {
      const response = await amplifyApi.createSubscriptionCheckout(tier);

      if (response.success) {
        if (response.type === 'portal') {
          // User already has subscription, redirect to portal
          window.location.href = response.portalUrl;
        } else if (response.type === 'checkout') {
          // Redirect to Stripe Checkout
          window.location.href = response.checkoutUrl;
        }
      } else {
        setError(response.error || 'Failed to create checkout session');
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    }

    setLoading(false);
  };

  const handleManageSubscription = async () => {
    setLoading('manage');
    setError(null);

    try {
      const response = await amplifyApi.createSubscriptionPortal();

      if (response.success && response.portalUrl) {
        window.location.href = response.portalUrl;
      } else {
        setError(response.error || 'Failed to open subscription portal');
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    }

    setLoading(false);
  };

  const tiers = ['free', 'pro', 'enterprise'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link
            to={brand?.routes?.studio || '/create'}
            className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">Back to Studio</span>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Page Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-full text-sm font-medium mb-4">
            <Zap className="w-4 h-4" />
            Unlock More Creative Power
          </div>
          <h1 className="text-4xl font-bold text-slate-900 mb-4">
            Choose Your Plan
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Create more products, generate more AI designs, and grow your merchandise business.
          </p>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="max-w-md mx-auto mb-8 flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
            <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">
              &times;
            </button>
          </div>
        )}

        {/* Subscription Status Banner */}
        {subscriptionStatus?.hasSubscription && (
          <div className="max-w-md mx-auto mb-8 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-800">
                  Active {currentTier.charAt(0).toUpperCase() + currentTier.slice(1)} Subscription
                </p>
                {subscriptionStatus.cancelAtPeriodEnd && (
                  <p className="text-xs text-green-600 mt-1">
                    Cancels at period end
                  </p>
                )}
              </div>
              <button
                onClick={handleManageSubscription}
                disabled={loading === 'manage'}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-green-700 hover:bg-green-100 rounded-lg transition-colors"
              >
                {loading === 'manage' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ExternalLink className="w-4 h-4" />
                )}
                Manage
              </button>
            </div>
          </div>
        )}

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {tiers.map((tierKey) => {
            const tier = TIER_CONFIG[tierKey];
            const TierIcon = tier.icon;
            const isCurrentTier = tierKey === currentTier;
            const isUpgrade = tiers.indexOf(tierKey) > tiers.indexOf(currentTier);
            const isLoading = loading === tierKey;

            return (
              <div
                key={tierKey}
                className={`relative bg-white rounded-2xl border-2 p-6 transition-all ${
                  tier.popular
                    ? 'border-blue-500 shadow-xl shadow-blue-100 scale-105'
                    : isCurrentTier
                      ? 'border-green-500 bg-green-50'
                      : 'border-slate-200 hover:border-slate-300 hover:shadow-lg'
                }`}
              >
                {/* Popular Badge */}
                {tier.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-blue-500 text-white text-sm font-medium rounded-full">
                    Most Popular
                  </div>
                )}

                {/* Current Plan Badge */}
                {isCurrentTier && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-green-500 text-white text-sm font-medium rounded-full">
                    Current Plan
                  </div>
                )}

                {/* Tier Header */}
                <div className="text-center mb-6 pt-2">
                  <div className={`w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-4 ${
                    tierKey === 'free' ? 'bg-slate-100' :
                    tierKey === 'pro' ? 'bg-blue-100' : 'bg-purple-100'
                  }`}>
                    <TierIcon className={`w-7 h-7 ${
                      tierKey === 'free' ? 'text-slate-600' :
                      tierKey === 'pro' ? 'text-blue-600' : 'text-purple-600'
                    }`} />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">{tier.name}</h3>
                  <p className="text-sm text-slate-500 mt-1">{tier.description}</p>
                  <div className="mt-4">
                    <span className="text-4xl font-bold text-slate-900">{tier.price}</span>
                    <span className="text-slate-500">{tier.period}</span>
                  </div>
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-8">
                  {tier.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                        tierKey === 'free' ? 'text-slate-400' :
                        tierKey === 'pro' ? 'text-blue-500' : 'text-purple-500'
                      }`} />
                      <span className="text-sm text-slate-600">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* Action Button */}
                {isCurrentTier ? (
                  <button
                    disabled
                    className="w-full py-3 px-4 rounded-lg bg-green-100 text-green-700 font-medium cursor-not-allowed"
                  >
                    Current Plan
                  </button>
                ) : tierKey === 'free' ? (
                  <button
                    disabled
                    className="w-full py-3 px-4 rounded-lg bg-slate-100 text-slate-400 font-medium cursor-not-allowed"
                  >
                    Free Forever
                  </button>
                ) : isUpgrade ? (
                  <button
                    onClick={() => handleUpgrade(tierKey)}
                    disabled={isLoading}
                    className={`w-full py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
                      tierKey === 'pro'
                        ? 'bg-blue-600 hover:bg-blue-700 text-white'
                        : 'bg-purple-600 hover:bg-purple-700 text-white'
                    } ${isLoading ? 'opacity-75 cursor-not-allowed' : ''}`}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Zap className="w-5 h-5" />
                        Upgrade to {tier.name}
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    onClick={handleManageSubscription}
                    disabled={loading === 'manage'}
                    className="w-full py-3 px-4 rounded-lg bg-slate-200 text-slate-600 font-medium hover:bg-slate-300 transition-colors"
                  >
                    {loading === 'manage' ? (
                      <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                    ) : (
                      'Manage Subscription'
                    )}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* FAQ Section */}
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-8">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            <div className="bg-white rounded-lg border border-slate-200 p-5">
              <h3 className="font-semibold text-slate-900 mb-2">Can I cancel anytime?</h3>
              <p className="text-slate-600 text-sm">
                Yes! You can cancel your subscription at any time. You'll continue to have access to your current plan until the end of your billing period.
              </p>
            </div>
            <div className="bg-white rounded-lg border border-slate-200 p-5">
              <h3 className="font-semibold text-slate-900 mb-2">What happens to my products if I downgrade?</h3>
              <p className="text-slate-600 text-sm">
                Your existing products remain unchanged. You simply won't be able to create new products beyond your new tier's limits.
              </p>
            </div>
            <div className="bg-white rounded-lg border border-slate-200 p-5">
              <h3 className="font-semibold text-slate-900 mb-2">Do limits reset?</h3>
              <p className="text-slate-600 text-sm">
                Daily limits reset every 24 hours, and monthly limits reset on your billing date. Session limits reset when you start a new product creation session.
              </p>
            </div>
            <div className="bg-white rounded-lg border border-slate-200 p-5">
              <h3 className="font-semibold text-slate-900 mb-2">Is there a free trial?</h3>
              <p className="text-slate-600 text-sm">
                The Free tier is always available with no time limit. Try it out, and upgrade when you need more power!
              </p>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12">
          <p className="text-slate-500 mb-4">
            Questions? <Link to="/contact" className="text-blue-600 hover:text-blue-700">Contact our team</Link>
          </p>
          <Link
            to={brand?.routes?.studio || '/create'}
            className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to creating
          </Link>
        </div>
      </div>
    </div>
  );
}

export default PricingPage;
